import {
  Box,
  Flex,
  Grid,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Stack,
} from '@chakra-ui/react';
import { useLogoutUserByAdmin } from '~/common/api/zeiterfassungServerApi/AuthApi';
import { Button, Heading, Text } from '~/common/components/defaultUIComponents';
import SpecialJobApi from '~/common/api/zeiterfassungServerApi/SpecialJobApi';
import { RoutineApi } from '~/common/api/zeiterfassungServerApi/RoutineApi';
import EmployeeApi from '~/common/api/zeiterfassungServerApi/EmployeeApi';
import { NewJobApi } from '~/common/api/zeiterfassungServerApi/NewJobApi';
import { CheckCircleIcon, WarningIcon, TimeIcon } from '@chakra-ui/icons';
import { JobApi } from '~/common/api/zeiterfassungServerApi/JobApi';
import ProjectApi from '~/common/api/zeiterfassungServerApi/ProjectApi';
import { formatName } from '~/common/utils/employeeUtils';
import useTranslate from '~/common/utils/translate';
import { Paper } from '~/common/components/Paper';
import React, { useMemo, useState } from 'react';
import _ from 'lodash';

type JobType = 'RJ' | 'RR' | 'RS' | 'NJ' | 'RP';

interface EmployeeWorkingInfoProps {
  current_job: number;
  current_job_type: JobType | null | undefined;
}

const textKeyByType = {
  RJ: 'current.job',
  RR: 'current.routine',
  RS: 'current.special',
  NJ: 'current.new_job',
  RP: 'current.project',
};

const EmployeeWorkingInfo: React.FC<EmployeeWorkingInfoProps> = (props) => {
  const translate = useTranslate();
  const [isOpen, setOpen] = useState(false);
  const [{ data: job }] = JobApi.hooks.useGetJobQuery(
    { id: props.current_job },
    {
      enabled: props.current_job_type === 'RJ' && isOpen,
    },
  );
  const [{ data: routine }] = RoutineApi.hooks.useRoutineQuery(
    { id: props.current_job },
    {
      enabled: props.current_job_type === 'RR' && isOpen,
    },
  );
  const { data: specialJob } = SpecialJobApi.hooks.useSpecialJobQuery(
    { id: props.current_job },
    {
      enabled: props.current_job_type === 'RS' && isOpen,
    },
  );
  const [{ data: newJob }] = NewJobApi.hooks.useGetNewJobQuery(
    { id: props.current_job },
    {
      enabled: props.current_job_type === 'NJ' && isOpen,
    },
  );
  const [{ data: project }] = ProjectApi.hooks.useProjectQuery(
    { id: props.current_job },
    {
      enabled: props.current_job_type === 'RP' && isOpen,
    },
  );

  return (
    <Popover isOpen={isOpen}>
      <PopoverTrigger>
        <Box
          onMouseEnter={() => !!props.current_job_type && setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          {props.children}
        </Box>
      </PopoverTrigger>
      <PopoverContent>
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverHeader>
          {translate(textKeyByType[props.current_job_type as JobType])}
        </PopoverHeader>
        <PopoverBody>
          {props.current_job_type === 'RJ' && (
            <Stack>
              <Text>{job?.name}</Text>
              <Text>{job?.description}</Text>
            </Stack>
          )}
          {props.current_job_type === 'RR' && (
            <Stack>
              <Text>{routine?.name}</Text>
              <Text>{routine?.description}</Text>
            </Stack>
          )}
          {props.current_job_type === 'RS' && (
            <Stack>
              <Text>{specialJob?.name}</Text>
              <Text>{specialJob?.description}</Text>
            </Stack>
          )}
          {props.current_job_type === 'NJ' && (
            <Stack>
              <Text>{newJob?.name}</Text>
              <Text>{newJob?.description}</Text>
            </Stack>
          )}
          {props.current_job_type === 'RP' && (
            <Stack>
              <Text>{project?.name}</Text>
            </Stack>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

interface EmployeeStatusProps {
  name: string;
  employee: { id: number };
  status: {
    active: boolean;
    is_in_pause: boolean;
    current_job: number | null | undefined;
    current_job_type: 'RJ' | 'RR' | 'RS' | 'NJ' | 'RP';
  };
}

const EmployeeStatus: React.FC<EmployeeStatusProps> = (props) => {
  const { mutate: logoutUser } = useLogoutUserByAdmin();
  return (
    <EmployeeWorkingInfo
      current_job={props.status.current_job as number}
      current_job_type={props.status.current_job_type}
    >
      <Paper
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        height="100%"
      >
        <Flex>
          <Box pr={2}>
            {props.status.active && !props.status.is_in_pause && (
              <CheckCircleIcon color="green.500" w={6} h={6} />
            )}
            {props.status.active && props.status.is_in_pause && (
              <TimeIcon
                color="white"
                borderRadius={'50%'}
                backgroundColor={'blue.500'}
                padding={1}
                w={6}
                h={6}
              />
            )}
            {!props.status.active && (
              <WarningIcon color="red.600" w={6} h={6} />
            )}
          </Box>
          <Text>{props.name}</Text>
        </Flex>
        {(props.status.active || props.status.is_in_pause) && (
          <Button
            m={0}
            onClick={() => logoutUser({ employee_id: props.employee.id })}
          >
            dashboard.employees.logout
          </Button>
        )}
      </Paper>
    </EmployeeWorkingInfo>
  );
};

export const ActiveEmployees: React.FC = () => {
  const [{ data: employees }] = EmployeeApi.hooks.useEmployeeQuery();
  const [{ data: employeeStatus }] = EmployeeApi.hooks.useCurrentEmployeeStatus(
    { refetchInterval: 1000 * 60 },
  );

  const employeesStats = useMemo(() => {
    if (!employees || !employeeStatus) {
      return [];
    }
    const stats = [];
    for (const employee of employees) {
      const mapped = _.find(
        employeeStatus,
        (e) => e.employee === parseInt(employee.id),
      ) ?? {
        employee: parseInt(employee.id),
        is_in_pause: false,
        active: false,
      };
      stats.push({ employee, status: mapped, name: formatName(employee) });
    }

    return _.orderBy(stats, 'name', 'asc');
  }, [employees, employeeStatus]);

  return (
    <Box>
      <Box maxH="60vh" overflowY="auto">
        <Heading mb={3}>dashboard.employees.active</Heading>
        <Grid templateColumns="repeat(3, 1fr)" gap={3} mb={5}>
          {employeesStats.map((e) => (
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            <EmployeeStatus key={e.employee.id} {...(e as any)} />
          ))}
        </Grid>
      </Box>
      <Flex direction="row" mt={1}>
        <Flex gap={2}>
          <Text>dasboard.employees.active_status</Text>
          <CheckCircleIcon color="green.500" w={6} h={6} />
          <Text>dasboard.employees.break_status</Text>
          <TimeIcon
            color="white"
            borderRadius={'50%'}
            backgroundColor={'blue.500'}
            padding={1}
            w={6}
            h={6}
          />
          <Text>dasboard.employees.left_status</Text>
          <WarningIcon color="red.600" w={6} h={6} />
        </Flex>
      </Flex>
    </Box>
  );
};
